import { useEffect, useState } from 'react';

import { desktopDownloadsPath } from './env';

export type DesktopOs = 'macos' | 'windows' | 'linux';

export interface DesktopDownload {
  /** i18n key suffix under `download.formats`. */
  format: string;
  /** File extension shown next to the label, e.g. ".dmg". */
  extension: string;
  href: string;
}

/**
 * Installers per OS, primary first. The file names are the stable aliases the
 * server keeps pointed at the latest release, so these links never go stale.
 */
export const desktopDownloads: Record<DesktopOs, DesktopDownload[]> = {
  macos: [
    {
      format: 'macosUniversal',
      extension: '.dmg',
      href: `${desktopDownloadsPath}/WorkingClock-macos-universal.dmg`,
    },
  ],
  windows: [
    {
      format: 'windowsInstaller',
      extension: '.exe',
      href: `${desktopDownloadsPath}/WorkingClock-windows-x64-setup.exe`,
    },
    { format: 'windowsMsi', extension: '.msi', href: `${desktopDownloadsPath}/WorkingClock-windows-x64.msi` },
  ],
  linux: [
    {
      format: 'linuxAppImage',
      extension: '.AppImage',
      href: `${desktopDownloadsPath}/WorkingClock-linux-amd64.AppImage`,
    },
    { format: 'linuxDeb', extension: '.deb', href: `${desktopDownloadsPath}/WorkingClock-linux-amd64.deb` },
    { format: 'linuxRpm', extension: '.rpm', href: `${desktopDownloadsPath}/WorkingClock-linux-x86_64.rpm` },
  ],
};

export const desktopOses: DesktopOs[] = ['macos', 'windows', 'linux'];

/**
 * Best guess at the visitor's desktop OS, or null on phones, tablets and
 * anything unrecognised (there is no desktop build to offer them).
 */
export function detectDesktopOs(userAgent: string, platform = ''): DesktopOs | null {
  const ua = userAgent.toLowerCase();
  if (/android|iphone|ipad|ipod|mobile/.test(ua)) return null;
  const hint = `${platform} ${ua}`.toLowerCase();
  if (hint.includes('win')) return 'windows';
  if (hint.includes('mac')) {
    // iPadOS reports a Mac user agent; a touch screen gives it away.
    if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) return null;
    return 'macos';
  }
  if (hint.includes('linux') || hint.includes('x11')) return 'linux';
  return null;
}

/**
 * The visitor's OS, resolved after mount only. The page is pre-rendered, so
 * the first client render must match the neutral server HTML; detecting during
 * render would leave React hydrating against different markup.
 */
export function useDetectedDesktopOs(): DesktopOs | null {
  const [os, setOs] = useState<DesktopOs | null>(null);
  useEffect(() => {
    const data = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
    setOs(detectDesktopOs(navigator.userAgent, data?.platform ?? navigator.platform ?? ''));
  }, []);
  return os;
}

/**
 * The current release's version, read from the updater manifest after mount.
 * Null until it arrives, and for good if the request fails: the page reads
 * fine without it.
 */
export function useLatestDesktopVersion(): string | null {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${desktopDownloadsPath}/latest.json`, { signal: controller.signal, cache: 'no-cache' })
      .then((res) => (res.ok ? (res.json() as Promise<{ version?: unknown }>) : null))
      .then((manifest) => {
        if (manifest && typeof manifest.version === 'string' && /^\d+\.\d+\.\d+/.test(manifest.version)) {
          setVersion(manifest.version);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);
  return version;
}
